/**
 * useLosses Hook
 *
 * React Query hooks for losses.
 * @see Sprint 13 - Pertes
 * @see US-13.6 - UI Page Pertes
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  lossesService,
  type QueryLossesDto,
  type CreateLossDto,
  type CreateFinishedProductLossDto,
} from '../services/losses.service';

/**
 * Query key factory for losses
 */
export const lossKeys = {
  all: ['losses'] as const,
  lists: () => [...lossKeys.all, 'list'] as const,
  list: (params: QueryLossesDto) => [...lossKeys.lists(), params] as const,
  details: () => [...lossKeys.all, 'detail'] as const,
  detail: (id: string) => [...lossKeys.details(), id] as const,
  kpis: () => [...lossKeys.all, 'kpis'] as const,
};

/**
 * Hook to fetch losses list with pagination
 */
export function useLosses(params: QueryLossesDto) {
  return useQuery({
    queryKey: lossKeys.list(params),
    queryFn: () => lossesService.list(params),
    placeholderData: (prev) => prev,
  });
}

/**
 * Hook to fetch losses KPIs
 */
export function useLossesKPIs() {
  return useQuery({
    queryKey: lossKeys.kpis(),
    queryFn: () => lossesService.getKPIs(),
  });
}

/**
 * Hook to fetch single loss detail
 */
export function useLoss(id: string | null) {
  return useQuery({
    queryKey: lossKeys.detail(id!),
    queryFn: () => lossesService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to create a loss
 */
export function useCreateLoss() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateLossDto) => lossesService.create(data),
    onSuccess: (loss) => {
      queryClient.invalidateQueries({ queryKey: lossKeys.all });
      // Also invalidate stock movements and current stock
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      queryClient.invalidateQueries({ queryKey: ['current-stock'] });
      toast.success(`Perte enregistree pour ${loss.rawProduct.name}`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de l\'enregistrement');
    },
  });
}

/**
 * Hook to cancel a loss
 */
export function useCancelLoss() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => lossesService.cancel(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: lossKeys.all });
      // Also invalidate stock movements and current stock
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      queryClient.invalidateQueries({ queryKey: ['current-stock'] });
      toast.success(`Perte annulee pour ${result.cancelledLoss.rawProduct.name}`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de l\'annulation');
    },
  });
}

// ========================================
// PERTES DE PRODUITS FINIS
// ========================================

/**
 * Hook to search PosProducts with recipes
 */
export function usePosProductsWithRecipe(search: string, enabled: boolean = true) {
  return useQuery({
    queryKey: [...lossKeys.all, 'pos-products', search],
    queryFn: () => lossesService.searchPosProductsWithRecipe(search),
    enabled: enabled && search.length >= 2,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to preview finished product loss
 */
export function useFinishedProductLossPreview(
  posProductId: string | null,
  quantity: number
) {
  return useQuery({
    queryKey: [...lossKeys.all, 'finished-preview', posProductId, quantity],
    queryFn: () => lossesService.previewFinishedProductLoss(posProductId!, quantity),
    enabled: !!posProductId && quantity > 0,
    staleTime: 10000, // 10 seconds
  });
}

/**
 * Hook to create a finished product loss
 */
export function useCreateFinishedProductLoss() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFinishedProductLossDto) =>
      lossesService.createFinishedProductLoss(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: lossKeys.all });
      // Also invalidate stock movements and current stock
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      queryClient.invalidateQueries({ queryKey: ['current-stock'] });
      toast.success(
        `Perte enregistree: ${result.posProductName} x${result.quantity} (${result.movements.length} ingredients)`
      );
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de l\'enregistrement');
    },
  });
}
